import {
  NOT
} from '../Boolean.lib';
import Core from '../Core';
import globalErrorHandler from '../Error/globalErrorHandler.fun';
import {
  joinHTML
} from '../HTML.lib';
import {
  Obj
} from '../Object.lib';
import {
  compileText,
  trim
} from '../String.lib';

/**
 * Opens a new browser window with optional content.
 *
 * @async
 * @function openWindow
 * @param {Object} params - Function parameters.
 * @param {string} [params.url=''] - URL to load in the new window.
 * @param {string} [params.title='10x10'] - Title for the new window.
 * @param {string|string[]} [params.content=''] - HTML content to write
 *   inside the new window if no URL or for dynamic content.
 * @param {Object} [params.options] - Optional window features, such as
 *   height, width, top, left, resizable, menubar, toolbar, location,
 *   status, scrollbars, etc. Default features can be overridden.
 * @returns {Promise<Window|null>} Returns the window object if opened,
 *   or null if popup was blocked or an error occurred.
 *
 * @description
 * - Merges default window features with user-provided options.
 * - Opens a new window using `window.open`.
 * - If `content` is provided, writes compiled HTML into the new window.
 * - Handles popup blockers and logs errors via `globalErrorHandler`.
 */
export default async function openWindow({
  title = '10x10',
  url = '',
  content = '',
  source = 'openWindow',
  ...options
}) {
  try {
    title = trim(title);
    url = trim(url);
    content = trim(content);
    const features = mapOptions({
      top: -window.screen.availHeight,
      left: window.screen.availWidth * 2,
      width: 900,
      height: 1200,
      resizable: true,
      menubar: false,
      toolbar: false,
      location: false,
      status: false,
      scrollbars: true,
      ...options
    });
    if (url) {
      content = compileText(
        '<iframe title="{{TITLE}}" src="{{URL}}#view=fitH&toolbar=1" frameborder="0"></iframe>',
        {
          TITLE: title,
          URL: url,
        }
      );
    }
    if (NOT(content)) {
      Core.showWarning('Content is not available');
      return null;
    }
    const style = '<style>body,html{margin:0;height:100%}iframe{border:0;width:100%;height:100%;}</style>';
    content = joinHTML(compileText(
      '<!DOCTYPE html><html><head><title>{{TITLE}}</title>{{STYLE}}</head><body>{{CONTENT}}</body></html>',
      {
        TITLE: title,
        STYLE: style,
        CONTENT: content,
      }
    ));
    const browserWindow = window.open(
      url,
      title,
      features
    );
    if (NOT(browserWindow)) {
      Core.showError('Browser blocking popup, please allow and then retry, thanks');
      return null;
    }
    browserWindow.opener = null; // equivalent to rel="noopener"
    console.debug('Opening window with content:', content);
    const doc = Obj(browserWindow.document);
    doc.open();
    doc.write(content);
    doc.close();
    return browserWindow;
  }
  catch (error) {
    globalErrorHandler(error);
  }
  return null;
}

function mapOptions(options = {}) {
  return Object.entries(options).map(([key, value]) => {
    // Convert booleans to yes/no
    if (typeof value === 'boolean') {
      return `${key}=${value ? 'yes' : 'no'}`;
    }
    return `${key}=${value}`;
  }).join(',');
}
